import '../../styles/styles.css';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';

import Layout from '../../components/Layout';

import AnimateIn from '../../components/AnimateIn';
import VimeoVideo from '../../components/VimeoVideo';
import Image from '../../components/Image';
import ColumnLists from '../../components/ColumnLists';
import LinkButton from '../../components/LinkButton';
import LinkList from '../../components/LinkList';
import Quote from '../../components/Quote';
import WorkHeader from '../../components/WorkHeader';

import { getProject } from '../../config/general';
import hardcodedStaticProps from '../../lib/harcodedStaticProps';

const Newmotion = ({featuredProjectsList}) => {
  const project = getProject('newmotion');

  return (
    <Layout metaData={{ data: project }} featuredProjectsList={featuredProjectsList}>
      <article className="components page">
        <WorkHeader component={{ project }} />

        <VimeoVideo
          className="g-row"
          component={{
            posterVideo: '325009005'
          }}
        />

        <AnimateIn className="text g-row">
          <h2 id="finding-focus-amidst-the-hypergrowth">
            Finding focus amidst the hypergrowth
          </h2>
          <p>
            In 2009, NewMotion was founded with the ambition to power the
            world’s shift to e-mobility. Today, NewMotion is a multinational
            company with the largest EV charging network in Europe and provides
            charging solutions at enterprise level.
          </p>
          <p>
            We were brought in to help NewMotion find its communication focus
            and to produce a corporate site that expands NewMotion’s footprint
            in the B2B sector.
          </p>
        </AnimateIn>

        <Image
          component={{
            image:
              '//images.yourmajesty.co/20190318155656/1920x1080-cover.png',
            caption:
              'We launched the new NewMotion websites in seven markets in the same week.'
          }}
        />

        <AnimateIn className="text g-row">
          <h2 id="newmotion-powering-the-e-mobility-movement">
            NewMotion: Powering the e-mobility movement
          </h2>
          <p>
            Six months later, we reintroduced NewMotion to the world with seven
            localized websites presenting a refreshed look along propositions
            that reflect their new position—to provide tailor-made, neatly
            packaged solutions for all business charging needs.
          </p>
        </AnimateIn>

        <Quote
          className="g-row quote--marks"
          component={{
            quote:
              'Your Majesty helped us distill and solidify our marketing focus. Our corporate site now reflects our ambition, matches our scale, and most importantly, allows us to scale up fast and efficiently',
            quotee: 'Gwendoline Harper Michon — International Marketing Manager'
          }}
        />

        <AnimateIn className="text g-row">
          <h2 id="change-and-alignment-from-within">
            Change and alignment from within
          </h2>
          <p>
            NewMotion’s breadth of services, while being impressive, could
            create difficulties for internal teams to communicate the offerings
            consistently. To find a unifying voice, we facilitated a series of
            strategy workshops to guide all stakeholders towards one direction.
          </p>
        </AnimateIn>

        <Image
          className="g-row"
          component={{
            image:
              '//images.yourmajesty.co/20190322084518/1920x1080-workshop.png'
          }}
        />

        <AnimateIn className="text g-row">
          <h2 id="standing-out-as-the-leader">Standing out as the leader</h2>
          <p>
            The EV charging industry is relatively new, yet already homogeneous
            when it comes to its visual expression.
          </p>
          <p>
            To highlight NewMotion’s unique offering beyond hardware, we created
            an abstract direction that places their products in the context of
            their other smart services, reframing NewMotion from being a product
            company to being a service company.
          </p>
        </AnimateIn>

        <Image
          component={{
            image:
              '//images.yourmajesty.co/20190322084339/1920x4758-screens1.png',
            calculateRatio: '1920,4758'
          }}
        />

        <AnimateIn className="text g-row">
          <h2 id="modular-and-scalable-by-design">
            Modular and scalable by design
          </h2>
          <p>
            The design system consists of versatile modules and components that
            are editable using the site's CMS, enabling local marketers to
            create and launch new sites quickly.
          </p>
        </AnimateIn>

        <Image
          component={{
            image:
              '//images.yourmajesty.co/20190322083820/1920x2781-modular1.png',
            calculateRatio: '1920,1745'
          }}
        />

        <LinkButton
          className="g-row"
          component={{
            url: 'https://newmotion.com/',
            title: 'Visit NewMotion.com'
          }}
        />

        <AnimateIn className="text g-row">
          <h2 id="looking-for-digital-solutions-for-your-firm-">
            Looking for digital solutions for your firm?
          </h2>
          <p>
            Email us at{' '}
            <a href="mailto:hello@yourmajesty.co">
              hello@yourmajesty.co
            </a>{' '}
            to book a consultation on your next project.
          </p>
        </AnimateIn>

        <ColumnLists
          className="g-row"
          component={{
            title: 'Services',
            columns: [
              {
                title: 'Strategy',
                items: [
                  'Brand Strategy',
                  'Collaborative Working Sessions',
                  'Communications Strategy',
                  'Content Strategy',
                  'Creative Strategy',
                  'Experience Mapping',
                  'Interviews & User Testing',
                  'Launch Strategy',
                  'Product Strategy',
                  'Research',
                  'Roadmapping',
                  'Technical Strategy',
                  'UX Strategy',
                  'Workshop & Facilitation'
                ]
              },

              {
                title: 'Design',
                items: [
                  'Art Direction',
                  'Creative Direction',
                  'Motion Design',
                  'UI/UX Design',
                  'Visual Design'
                ]
              },

              {
                title: 'Technology',
                items: [
                  'Back-end Development',
                  'Content Management System',
                  'DevOps',
                  'eCommerce',
                  'Front-end Development',
                  'Localization',
                  'Systems Architecture'
                ]
              },

              {
                title: 'Delivery',
                items: [
                  'Analytics & Insights',
                  'Integration Support',
                  'Launch',
                  'Monitoring',
                  'Optimization',
                  'Quality Assurance',
                  'Quantitative and Qualitative Analysis',
                  'Staff Training & Support'
                ]
              }
            ]
          }}
        />
      </article>
    </Layout>
  );
};

export { hardcodedStaticProps as getStaticProps };

export default Newmotion;
